import { useEffect, useState } from "react";
import { Grid, Portal, TablePagination, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import { useIndicators } from "hooks/indicatorHooks";
import { usePagination } from "hooks/tableHooks";
import { useSelection, useUpdateSelectionMutation } from "hooks/selectionHooks";

import { IndicatorBase, LayerProperties, PAGINATION_OPTIONS } from "types";

import { LayerCard } from "./LayerCard";
import { IndicatorsCardsGrid } from "layout/IndicatorsCardsGrid";
import {
  addIndicatorToSelection,
  removeIndicatorFromSelection,
} from "utils/IndicatorSelectionUtils";

interface Props {
  config: string;
  layerProperties: LayerProperties[];
  search: string;
  selectedTags: string[];
  pagerContainer: Element;
}

export function LayerCardsGrid(props: Props) {
  const { config, layerProperties, search, selectedTags, pagerContainer } =
    props;
  const { t } = useTranslation();
  const [previousTagLength, setPreviousTagLength] = useState(0);
  const [pagination, setPagination, onChangePage, onChangeRowsPerPage] =
    usePagination(
      {
        data: [],
        offset: 0,
        limit: 10,
        totalCount: 0,
      },
      PAGINATION_OPTIONS
    );
  const { data: indicators } = useIndicators(
    config,
    {
      limit: pagination.limit,
      offset: pagination.offset,
      tags: selectedTags,
      search: search,
    },
    layerProperties
  );

  const { data: indicatorSelection } = useSelection(config, layerProperties);
  const updateSelectionMutation = useUpdateSelectionMutation(
    config,
    layerProperties
  );

  /**
   * When the tag selection changes, we reset the pagination to page 0, so the user starts over.
   * Validate the previousLength vs current, otherwise the effect triggers to much.
   *
   * //FIXME: make the useEffect disappear: https://beta.reactjs.org/learn/you-might-not-need-an-effect
   */
  useEffect(() => {
    if (previousTagLength !== selectedTags.length) {
      setPagination({ ...pagination, offset: 0 });
      setPreviousTagLength(selectedTags.length);
    }
  }, [selectedTags.length, previousTagLength, pagination, setPagination]);

  function onAddToSelection(indicator: IndicatorBase) {
    // can't add when selection not avaialble
    if (!indicatorSelection) return;

    const updatedSelection = addIndicatorToSelection(
      indicatorSelection,
      indicator
    );
    updateSelectionMutation.mutateAsync(updatedSelection);
  }

  function onRemoveFromSelection(indicator: IndicatorBase) {
    // can't add when selection not avaialble
    if (!indicatorSelection) return;

    const updatedSelection = removeIndicatorFromSelection(
      indicatorSelection,
      indicator
    );
    updateSelectionMutation.mutateAsync(updatedSelection);
  }

  return render();

  function render() {
    if (!indicators) return null;
    if (indicators.totalCount === 0) return renderNoLayersFound();
    else
      return (
        <IndicatorsCardsGrid>
          {renderLayers(indicators.data)}
          {renderPagination()}
        </IndicatorsCardsGrid>
      );
  }

  function renderLayers(indicators: IndicatorBase[]) {
    return (
      <>
        {indicators.map((indicator) => (
          <LayerCard
            key={indicator.id}
            indicator={indicator}
            selection={indicatorSelection?.indicators || []}
            onAddToSelection={() => onAddToSelection(indicator)}
            onRemoveFromSelection={() => onRemoveFromSelection(indicator)}
          />
        ))}
      </>
    );
  }

  function renderNoLayersFound() {
    return (
      <Grid item xs={12}>
        <Typography
          color="error"
          className={[
            selectedTags.length < 1 ? "empty-tags" : "tags",
            !search ? "empty-filter" : "search",
          ].join(" ")}
        >
          <Trans
            i18nKey="label.noMapLayersFoundPleaseCheckSearch"
            tOptions={{ tags: selectedTags.join(", "), search }}
            components={{
              li: <li></li>,
              br: <br />,
              strong: <strong></strong>,
              ul: <ul></ul>,
            }}
          />
        </Typography>
      </Grid>
    );
  }

  function renderPagination() {
    return (
      <Portal container={pagerContainer}>
        <TablePagination
          component="div"
          page={pagination.offset}
          rowsPerPage={pagination.limit}
          count={indicators?.totalCount || 0}
          rowsPerPageOptions={PAGINATION_OPTIONS}
          onRowsPerPageChange={onChangeRowsPerPage}
          onPageChange={onChangePage}
          labelRowsPerPage={t("label.rowsPerPage")}
        />
      </Portal>
    );
  }
}
